import { Helmet } from "react-helmet-async";
import React from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.doxandbox.com";

function safeJsonLd(schema) {
  if (!schema || typeof schema !== "string") {
    return null;
  }

  try {
    return JSON.stringify(JSON.parse(schema));
  } catch (error) {
    return null;
  }
}

function safeJsonLdItems(items) {
  return Array.isArray(items)
    ? items
        .map((item) => ({
          label: item?.label || "",
          schema: safeJsonLd(item?.schema),
        }))
        .filter((item) => item.schema)
    : [];
}

const SEO = ({ seoData, initialSeo, globalSchema }) => {
  const location = useLocation();
  const seo = seoData || initialSeo || {};
  const canonical =
    seo.canonical_tag ||
    seo.canonicalTag ||
    `${SITE_URL}${location.pathname === "/" ? "/" : location.pathname.replace(/\/$/, "")}`;
  const title = seo.title;
  const description = seo.description;
  const keywords = seo.keywords || "";
  const faqSchema = safeJsonLd(seo.faq_schema);
  const articleSchema = safeJsonLd(seo.article_schema);
  const reviewSchema = safeJsonLd(globalSchema?.review_schema);
  const organisationSchema = safeJsonLd(globalSchema?.organisation_schema);
  const additionalSchemas = safeJsonLdItems(seo.additional_schemas);
  const globalAdditionalSchemas = safeJsonLdItems(globalSchema?.additional_schemas);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />

      <meta property="og:title" content={seo.og_title || title} />
      <meta property="og:description" content={seo.og_description || description} />
      <meta property="og:url" content={seo.og_url || canonical} />
      {seo.og_image && <meta property="og:image" content={seo.og_image} />}
      <meta property="og:site_name" content={globalSchema?.og_site_name || seo.og_site_name || "Doxandbox"} />
      {seo.og_locale && <meta property="og:locale" content={seo.og_locale} />}
      <meta property="og:type" content={seo.og_type || "website"} />

      <meta name="twitter:card" content={globalSchema?.twitter_card || seo.twitter_card || ""} />
      {(globalSchema?.twitter_site || seo.twitter_site) && (
        <meta name="twitter:site" content={globalSchema?.twitter_site || seo.twitter_site} />
      )}
      <meta name="twitter:title" content={seo.twitter_title || title} />
      <meta name="twitter:description" content={seo.twitter_description || description} />
      {seo.twitter_image && <meta name="twitter:image" content={seo.twitter_image} />}
      {(globalSchema?.twitter_creator || seo.twitter_creator) && (
        <meta name="twitter:creator" content={globalSchema?.twitter_creator || seo.twitter_creator} />
      )}

      {faqSchema && <script type="application/ld+json">{faqSchema}</script>}
      {articleSchema && <script type="application/ld+json">{articleSchema}</script>}
      {additionalSchemas.map((item, index) => (
        <script
          key={`${item.label || "additional-schema"}-${index}`}
          type="application/ld+json"
          data-schema-label={item.label || undefined}
        >
          {item.schema}
        </script>
      ))}
      {reviewSchema && <script type="application/ld+json">{reviewSchema}</script>}
      {organisationSchema && <script type="application/ld+json">{organisationSchema}</script>}
      {globalAdditionalSchemas.map((item, index) => (
        <script
          key={`global-${item.label || "additional-schema"}-${index}`}
          type="application/ld+json"
          data-schema-label={item.label || undefined}
          data-schema-scope="global"
        >
          {item.schema}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
